import {
  FunctionPlugin,
  CellError,
  SimpleRangeValue,
  ArraySize,
  FunctionArgumentType,
} from 'hyperformula';

/**
 * Custom function plugin.
 */
export class MyCustomPlugin extends FunctionPlugin {
  // greet(ast, state) {
  //   return this.runFunction(
  //     ast.args,
  //     state,
  //     this.metadata('GREET'),
  //     (firstName) => {
  //       return `👋 Hello, ${firstName}!`;
  //     },
  //   );
  // }

  // doubleRange(ast, state) {
  //   return this.runFunction(
  //     ast.args,
  //     state,
  //     this.metadata('DOUBLE_RANGE'),
  //     (range) => {
  //       const rangeData = range.data;
  //       const dataAsNumbers = rangeData.map(row => row.map(val => this.internalScalarValueToNumber(val)));

  //       if (dataAsNumbers.some(row => row.some(val => val === undefined))) {
  //         return new CellError('VALUE', 'Function DOUBLE_RANGE operates only on numbers.');
  //       }

  //       const doubledData = dataAsNumbers.map(row => row.map(val => val * 2));

  //       // Ensure the result is a SimpleRangeValue
  //       return SimpleRangeValue.onlyValues(doubledData);
  //     },
  //   );
  // }

  trend(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('TREND'),
      (yRange, xRange, xValue) => {
        // Perform linear regression (y = mx + b)
        const yData = yRange.data.flat();
        const xData = xRange.data.flat();

        if (yData.length !== xData.length) {
          return new CellError(
            'VALUE',
            'Ranges for Y and X must have the same length.'
          );
        }

        // Calculate the necessary sums for linear regression
        const n = yData.length;
        const xSum = xData.reduce((sum, val) => sum + val, 0);
        const ySum = yData.reduce((sum, val) => sum + val, 0);
        const xSquareSum = xData.reduce((sum, val) => sum + val * val, 0);
        const xySum = xData.reduce(
          (sum, val, idx) => sum + val * yData[idx],
          0
        );

        // Calculate slope (m) and intercept (b) using the formula for linear regression
        const m = (n * xySum - xSum * ySum) / (n * xSquareSum - xSum * xSum);
        const b = (ySum - m * xSum) / n;

        // Predict yValue for the given xValue using y = mx + b
        const predictedYValue = m * xValue + b;

        return SimpleRangeValue.onlyValues([[predictedYValue]]);
      }
    );
  }

  // GROWTH function
  growth(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('GROWTH'),
      (yRange, xRange, xValue) => {
        const yData = yRange.data.flat();
        const xData = xRange.data.flat();

        if (yData.length !== xData.length) {
          return new CellError(
            'VALUE',
            'Ranges for Y and X must have the same length.'
          );
        }

        // Perform exponential regression (y = a * e^(b * x))
        const n = yData.length;
        const sumX = xData.reduce((sum, val) => sum + val, 0);
        const sumY = yData.reduce((sum, val) => sum + Math.log(val), 0); // Log transformation of y-values for linear regression
        const sumX2 = xData.reduce((sum, val) => sum + val * val, 0);
        const sumXY = xData.reduce(
          (sum, val, idx) => sum + val * Math.log(yData[idx]),
          0
        );

        // Calculate b and a for the equation y = a * e^(b * x)
        const b = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        const a = Math.exp((sumY - b * sumX) / n);

        // Calculate the predicted value using the formula
        const predictedYValue = a * Math.exp(b * xValue);

        return SimpleRangeValue.onlyValues([[predictedYValue]]);
      }
    );
  }

  // Implement INDIRECT function
  indirect(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('INDIRECT'),
      (refText) => {
        // refText is expected to be a string representing a cell/range reference, e.g., "A1" or "B2:B5"
        console.log(refText, 'ffffffffffffffffffff');
        
        // Try to resolve the reference
        try {
          const cellReference = this.resolveReference(refText);
          console.log(cellReference, 'gggggggggggggggggggggggggg');
          
          return SimpleRangeValue.onlyValues([[cellReference]]);
        } catch (e) {
          console.log(e.message, 'ssssssssssssssssssssssssss');
          
          return new CellError('VALUE', `Invalid reference: ${refText}`);
        }
      }
    );
  }

  irr(ast, state) {
    // Evaluate all arguments manually
    const evaluatedArgs = ast.args.map(arg => this.evaluateAst(arg, state));
  
    // Call the IRR logic manually now that we have evaluated inputs
    const inputArg = evaluatedArgs[0];
    const guessArg = evaluatedArgs[1];
    const guess = guessArg?.data?.[0] != null ? parseFloat(guessArg.data[0]) : 0.1;
  
    console.log("👋 Entered IRR function");
  
    const flat = inputArg?.data?.flat?.() || [];
    const cashflows = flat.map(v => {
      let raw = (typeof v === 'object' && v?.value !== undefined) ? v.value : v;
      return typeof raw === 'string' ? parseFloat(raw) : raw;
    });
  
    if (cashflows.some(v => typeof v !== 'number' || isNaN(v))) {
      return new CellError('VALUE', 'Invalid cashflow input');
    }
  
    const hasPositive = cashflows.some(v => v > 0);
    const hasNegative = cashflows.some(v => v < 0);
    if (!hasPositive || !hasNegative) {
      return new CellError('NUM', 'IRR requires at least one positive and one negative cash flow.');
    }
  
    let rate = guess;
    const maxIterations = 1000;
    const precision = 1e-7;
  
    const npv = (r) => cashflows.reduce((acc, val, i) => acc + val / Math.pow(1 + r, i), 0);
  
    for (let i = 0; i < maxIterations; i++) {
      const fVal = npv(rate);
      const fPrime = cashflows.reduce((acc, val, i) => acc - i * val / Math.pow(1 + rate, i + 1), 0);
  
      if (!isFinite(fPrime) || fPrime === 0) break;
  
      const newRate = rate - fVal / fPrime;
      if (!isFinite(newRate)) break;
  
      if (Math.abs(newRate - rate) < precision) {
        return `${Math.round(newRate * 100)}%`;
      }
      
  
      rate = newRate;
    }
  
    return new CellError('NUM', 'IRR did not converge');
  }

  irrResultArraySize(ast, state) {
    return ArraySize.scalar();
  }  


  errorType(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('ERROR_TYPE'),
      (value) => {
        const strVal = typeof value === 'string' ? value.toUpperCase() : null;

        const map = {
          '#NULL!': 1,
          '#DIV/0!': 2,
          '#VALUE!': 3,
          '#REF!': 4,
          '#NAME?': 5,
          '#NUM!': 6,
          '#N/A': 7,
          '#ERROR!': 8
        };

        if (strVal && map[strVal]) {
          return map[strVal];
        }

        if (value instanceof CellError) {
          const code = {
            NULL: 1,
            DIV_BY_ZERO: 2,
            VALUE: 3,
            REF: 4,
            NAME: 5,
            NUM: 6,
            NA: 7,
            ERROR: 8
          }[value.type];
          return code || new CellError('NA', 'Unknown error type');
        }
        return new CellError('NA', 'ERROR.TYPE expects an error literal or CellError');
      }
    );
  }

  modeSngl(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('MODE_SNGL'),
      (range) => {
        const flatValues = range.data.flat();
        const numericValues = flatValues.filter(v => typeof v === 'number' && isFinite(v));
  
        if (numericValues.length === 0) {
          return new CellError(ErrorType.NA, 'No numeric values found');
        }

        const freqMap = new Map();
        for (const num of numericValues) {
          freqMap.set(num, (freqMap.get(num) || 0) + 1);
        }
  
        const maxFreq = Math.max(...freqMap.values());
        const modes = [...freqMap.entries()].filter(([_, freq]) => freq === maxFreq);
        return modes[0][0];
      }
    );
  }
  
  mode(ast, state) {
    return this.runFunction(
      ast.args,
      state,
      this.metadata('MODE'),
      (range) => {
        const flatValues = range.data.flat();
        const numericValues = flatValues.filter(v => typeof v === 'number' && isFinite(v));

        // Check for no numeric values
        if (numericValues.length === 0) {
          return new CellError('NA', 'No numeric values found');
        }

        // Create a frequency map of values
        const freqMap = new Map();
        for (const num of numericValues) {
          freqMap.set(num, (freqMap.get(num) || 0) + 1);
        }

        // Find the maximum frequency
        const maxFreq = Math.max(...freqMap.values());
        const modes = [...freqMap.entries()].filter(([_, freq]) => freq === maxFreq);

        // If there are multiple modes, return the smallest mode
        return SimpleRangeValue.onlyValues([[Math.min(...modes.map(([val]) => val))]]);
      }
    );
  }
  modeResultArraySize(ast, state) {
    const arg = ast?.args?.[0];
    if (!arg?.start || !arg?.end) {
      return ArraySize.scalar(); // If range is invalid, return scalar
    }
    return ArraySize.scalar(); // MODE returns a single value
  }


  // internalScalarValueToNumber(value) {
  //   const rawValue = value.val ?? value;
  //   return typeof rawValue === 'number' ? rawValue : undefined;
  // }

  // // Correct the calculation of the range size to avoid spill error
  // doubleRangeResultArraySize(ast, state) {
  //   const arg = ast?.args?.[0];

  //   // Ensure that we handle the case when the range's start or end is undefined or null
  //   if (!arg?.start || !arg?.end) {
  //     return ArraySize.scalar(); // Return scalar if no valid range
  //   }

  //   // Calculate the width and height of the input range
  //   const width = arg.end.col - arg.start.col + 1;
  //   const height = arg.end.row - arg.start.row + 1;

  //   // Return the array size corresponding to the output of the double range operation
  //   return new ArraySize(width, height);
  // }

  // Correct the calculation of the range size for TREND function
  trendResultArraySize(ast, state) {
    const arg = ast?.args?.[0];

    if (!arg?.start || !arg?.end) {
      return ArraySize.scalar();
    }

    // For the TREND function, the output should be a scalar for each prediction
    return ArraySize.scalar();
  }

  // Correct the calculation of the range size for GROWTH function
  growthResultArraySize(ast, state) {
    const arg = ast?.args?.[0];

    if (!arg?.start || !arg?.end) {
      return ArraySize.scalar();
    }

    // For the GROWTH function, the output should be a scalar for each prediction
    return ArraySize.scalar();
  }

  // Resolve the reference (this will need to interact with the grid's internal data structure)
  resolveReference(refText) {
    // Assuming refText is a string like "A1", "B2:B5", etc.
    console.log(this.context, 'decravtwr');
    
    const sheet = this.context.sheet();
    const reference = sheet.getCell(refText);

    if (!reference) {
      throw new Error('Reference not found');
    }
    return reference.value; // Return the value from the resolved reference
  }

  // Correct the calculation of the range size for INDIRECT function
  indirectResultArraySize(ast, state) {
    const refText = ast?.args?.[0];
    if (!refText) {
      return ArraySize.scalar();
    }

    // Here, we assume INDIRECT refers to a single cell, so the result size is scalar
    return ArraySize.scalar();
  }
}

// Static property with the custom functions definitions
MyCustomPlugin.implementedFunctions = {
  // GREET: {
  //   method: 'greet',
  //   parameters: [
  //     { argumentType: FunctionArgumentType.STRING }
  //   ],
  // },
  // DOUBLE_RANGE: {
  //   method: 'doubleRange',
  //   arraySizeMethod: 'doubleRangeResultArraySize',
  //   parameters: [
  //     { argumentType: FunctionArgumentType.RANGE },
  //   ],
  // },
  TREND: {
    method: 'trend',
    arraySizeMethod: 'trendResultArraySize', // Handle the size of the TREND result
    parameters: [
      { argumentType: FunctionArgumentType.RANGE }, // Y range
      { argumentType: FunctionArgumentType.RANGE }, // X range
      { argumentType: FunctionArgumentType.NUMBER } // X value to predict for
    ]
  },
  GROWTH: {
    method: 'growth',
    arraySizeMethod: 'growthResultArraySize', // Handle the size of the GROWTH result
    parameters: [
      { argumentType: FunctionArgumentType.RANGE }, // Y range
      { argumentType: FunctionArgumentType.RANGE }, // X range
      { argumentType: FunctionArgumentType.NUMBER } // X value to predict for
    ]
  },
  INDIRECT: {
    method: 'indirect',
    arraySizeMethod: 'indirectResultArraySize', // Handle the size of the INDIRECT result
    parameters: [
      { argumentType: FunctionArgumentType.STRING } // refText as a string
    ]
  },
  IRR: {
    method: 'irr',
    arraySizeMethod: 'irrResultArraySize',
    parameters: [
      { argumentType: FunctionArgumentType.ANY },
      { argumentType: FunctionArgumentType.NUMBER, optional: true }
    ]
  },
  ERROR_TYPE: {
    method: 'errorType',
    parameters: [
      { argumentType: FunctionArgumentType.SCALAR } // DO NOT USE NUMBER/RANGE
    ]
  },
  MODE_SNGL: {
    method: 'modeSngl',
    parameters: [
      { argumentType: FunctionArgumentType.RANGE }
    ]
  },
  MODE: {
    method: 'mode',
    arraySizeMethod: 'modeResultArraySize',
    parameters: [
      { argumentType: FunctionArgumentType.RANGE }
    ]
  }
};

// Custom functions translations
export const MyCustomPluginTranslations = {
  enGB: {
    // GREET: 'GREET',
    // DOUBLE_RANGE: 'DOUBLE_RANGE',
    TREND: 'TREND',
    GROWTH: 'GROWTH',
    INDIRECT: 'INDIRECT',
    IRR: 'IRR',
    ERROR_TYPE: 'ERROR.TYPE',
    MODE_SNGL: 'MODE.SNGL',
    MODE: 'MODE'

  },
  enUS: {
    // GREET: 'GREET',
    // DOUBLE_RANGE: 'MAKE_TWICE',
    TREND: 'TREND',
    GROWTH: 'GROWTH',
    INDIRECT: 'INDIRECT',
    IRR: 'IRR',
    ERROR_TYPE: 'ERROR.TYPE',
    MODE_SNGL: 'MODE.SNGL',
    MODE: 'MODE',
  },
  translationMapping: {
    'MODE.SNGL': 'MODE_SNGL',
    'MODE' : 'MODE'
  }
};
